import { motion } from "framer-motion";

const CHAPTERS = [
  {
    num: "01",
    title: "The Anti-Fluff Protocol",
    body: "We don't sell impressions, likes, or dashboards full of decorative numbers. Every dollar you spend with us is wired to a revenue outcome — pipeline, bookings, signed deals. If a tactic can't be traced to growth, it doesn't make the plan.",
    testid: "manifesto-chapter-1",
  },
  {
    num: "02",
    title: "The California Architecture Mindset",
    body: "The Golden Gate wasn't decorated into existence — it was engineered. We build brands the same way: structural strategy first, beauty as a load-bearing element, nothing ornamental that doesn't also perform.",
    testid: "manifesto-chapter-2",
  },
  {
    num: "03",
    title: "Precision Over Volume",
    body: "We take on few clients and go unreasonably deep. Senior partners on every account, no hand-offs to junior pods, no templated playbooks. Your growth gets the same obsession we'd give our own company.",
    testid: "manifesto-chapter-3",
  },
  {
    num: "04",
    title: "The Infinite Growth Engine",
    body: "Great marketing compounds. Brand sharpens performance, performance funds brand, content feeds both. We architect systems where every channel makes the others stronger — growth that keeps building on itself.",
    testid: "manifesto-chapter-4",
  },
];

export const Manifesto = () => {
  return (
    <section id="manifesto" data-testid="manifesto-section" className="relative bg-[#0A1128] py-28 lg:py-36 overflow-hidden">
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -right-10 top-10 font-serif text-[16rem] leading-none text-stroke-gold select-none hidden lg:block"
      >
        BR
      </span>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mb-20"
        >
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber-400/90 mb-4">The Manifesto</p>
          <h2 className="font-serif font-bold tracking-tight leading-[1.05] text-3xl sm:text-5xl lg:text-6xl text-amber-50 max-w-2xl">
            How we think,
            <br />
            <span className="italic text-amber-300">in four chapters.</span>
          </h2>
        </motion.div>

        <div className="space-y-0">
          {CHAPTERS.map((c, i) => (
            <motion.article
              key={c.num}
              data-testid={c.testid}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
              className={`grid lg:grid-cols-12 gap-6 lg:gap-12 border-t border-amber-500/15 py-14 lg:py-16 ${
                i % 2 === 1 ? "lg:text-right" : ""
              }`}
            >
              <div className={`lg:col-span-3 ${i % 2 === 1 ? "lg:order-3" : ""}`}>
                <span className="font-serif text-6xl lg:text-7xl text-stroke-gold">{c.num}</span>
              </div>
              <div className="lg:col-span-4">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500/70 mb-3">
                  Chapter {c.num}
                </p>
                <h3 className="font-serif text-2xl lg:text-3xl font-semibold text-amber-100 leading-snug">
                  {c.title}
                </h3>
              </div>
              <div className="lg:col-span-5">
                <p className="text-base font-light leading-relaxed text-slate-300/90">{c.body}</p>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};
